import React, {useState, useEffect, useMemo} from 'react';
import { useSelector } from 'react-redux';
import { IStateType } from '../../../../../store/models/root.interface';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import Styles from './index.module.scss';
import {
	RangePicker,
	Button,
	Select,
	SelectOption,
	MultiSelectOption,
	MultiSelect,
	Slider
} from '@kaleyra/emmy';
import {debounce, isEmpty} from 'lodash';
import moment from 'moment';

export type MapStateToPropsType = {
	params:any,
	languageList:any
	speechIds:any
}
export type MapDispatchToPropsType = {
	closeFilterModal : () => void;
	applyFilters: (filters:any) => void
	loadSpeechIds: (filters:any) => void
}
const FilterTemplate : React.FC<MapDispatchToPropsType & MapStateToPropsType> = (
	{
		params,
		closeFilterModal,
		applyFilters,
		languageList,
		loadSpeechIds,
		speechIds
}) => {
	const [speechId, setSpeechId] = useState<string[]>([]);
	const [flowName, setFlowName] = useState<string[]>([]);
	const [language, setLanguage] = useState<string[]>( []);
	const [callType, setCallType] = useState<any>(null);
	const [dateRange, setDateRange] = useState([
		moment()
			.subtract(6, 'days')
			.startOf('day')
			.format('YYYY-MM-DD HH:mm:ss'),
		moment(Date.now()).format('YYYY-MM-DD HH:mm:ss'),
	]);
	const languagesListFiltered = useMemo(()=>{
		if(isEmpty(languageList)) return {}
		return languageList?.list;
	},[languageList])

	const [speechIdSearchValue, setSpeechIdSearchValue] = useState<string>("");
	const { t } = useTranslation();

	let {
		speechCallTypeList,
		ivrFlowNameList,
	} = useSelector((state: IStateType) => state.analyticsReportReducer);

	useEffect(() => {
		loadSpeechIds({});
	},[]);

	// let speechCallTypeList:Record<string, string> = {
	// 	"Incoming": "Incoming",
	// 	"Outgoing": "Outgoing"
	// } // TODO remove

	const {
		setFlowNameTags,
		setDateRangeTags,
		setSpeechIdTags,
		setCallTypeTag,
		dateRangeTags,
		flowNameTags,
		callTypeTag,
		speechIdTags,
		languageTags,
		setLanguageTags,
	} = params;

	useEffect(() => {
		if (!isEmpty(speechIdTags)) {
			setSpeechId(speechIdTags);
		}
		if (!isEmpty(dateRangeTags)) {
			setDateRange(dateRangeTags);
		}
		if (!isEmpty(flowNameTags)) {
			setFlowName(flowNameTags);
		}
		if (callTypeTag) {
			setCallType(callTypeTag);
		}
		if(!isEmpty(languageTags)){
			setLanguage(languageTags);
		}
	}, []);

	const handleDateChange = (date: any, dateString: any) => {
		setDateRange(dateString);
	};

	const handleFilterClick = () => {
		let filterData: any = {};
		if (!isEmpty(language)) filterData.language = language;
		if (!isEmpty(dateRange)) {
			filterData.dateRange = dateRange;
			setDateRangeTags([...dateRange]);
		}
		if (!isEmpty(flowName)) filterData.flow_name = flowName;
		if (!isEmpty(speechId)) filterData.id = speechId;
		if (callType) filterData.consuming_service = callType;

		setLanguageTags(language);
		setSpeechIdTags(speechId);
		setFlowNameTags(flowName);
		setCallTypeTag(callType);

		applyFilters(filterData);
		closeFilterModal();
	};

	const disableApplyBtn = useMemo(() => {
		if (!isEmpty(dateRange) && dateRange[0]) return false;
		if (!isEmpty(speechId)) return false;
		if (!isEmpty(flowName)) return false;
		if (!isEmpty(language)) return false;
		if (callType) return false;
		return true;
	},[dateRange, callType, speechId,language,flowName]);

	const format = 'YYYY-MM-DD HH:mm:ss';

	function disabledDate(current: any) {
		// Can not select days after today
		return (
			current &&
			(current > moment().endOf('day') ||
				current < moment().subtract(6, 'days').startOf('day'))
		);
	}

	const debouncedChange = React.useCallback(
		debounce((value:string = "") => {
			loadSpeechIds({
				limit: 25,
				offset: 0,
				call_id:value
			});
		}, 400),
		[]
	);
	return (
		<Slider
			title={t('mainFilter')}
			containerId="root-modal"
			onClose={() => closeFilterModal()}
			visibility={true}
			footer={(
				<div className={Styles.footer}>
					<span
						className={Styles.cancelBtn}
						onClick={closeFilterModal}
					>
						<Button type="tertiary">{t('mainCancel')}</Button>
					</span>
					<span onClick={handleFilterClick}>
						<Button disabled={disableApplyBtn} type="primary">
							{t('BLD01036')}
						</Button>
					</span>
				</div>
			)}
		>
			<div className={Styles.formWrapper}>
				<MultiSelect
					selectedValues={language}
					onChange={(val: any) => setLanguage(val)}
					placeholder={t('KLR00722')}
					defaultValue={isEmpty(language) ? languageTags : language}
					mode={'multiple'}
					className={"mb-3"}
				>
					{
						Object.keys(languagesListFiltered).map((key) => {
							return (
								<MultiSelectOption key={key} value={key}>
									{languagesListFiltered[key]}
								</MultiSelectOption>
							);
						})
					}
				</MultiSelect>
				<RangePicker
					// onOk={(date) => { handleDateChange(date) }}
					onChange={(date, dateString) => {
						handleDateChange(date, dateString);
					}}
					className={cn("mb-3",Styles.datePickerWrapper)}
					showTime={{
						hideDisabledOptions: true,
					}}
					format={format}
					defaultValue={
						isEmpty(dateRangeTags) || isEmpty(dateRangeTags[0])
							? [
								moment(dateRange[0], format),
								moment(dateRange[1], format),
							]
							: [
								moment(dateRangeTags[0], format),
								moment(dateRangeTags[1], format),
							]
					}
					disabledDate={disabledDate}
					placeholder={[t('startDate'), t('endDate')]}
				/>

				<MultiSelect
					selectedValues={flowName}
					onChange={(val: any) => setFlowName(val)}
					placeholder={t('mainFlowName')}
					defaultValue={isEmpty(flowName) ? flowNameTags : flowName}
					mode={'multiple'}
					className={"mb-3"}
				>
					{!isEmpty(ivrFlowNameList) &&
						Object.values(ivrFlowNameList).map((option) => {
							return (
								<MultiSelectOption key={option} value={option}>
									{option}
								</MultiSelectOption>
							);
						})}
				</MultiSelect>
				<Select
					value={callType}
					defaultValue={callTypeTag}
					label={t('callTypeSel')}
					placeholder={t('callTypeSel')}
					selectClassName={Styles.selectBox}
					onChange={(value: any) => {
						setCallType(value);
					}}
					className={"mb-3"}
					showBottomLabelSpace={false}
				>
					{Object.keys(speechCallTypeList).map((key:string) => {
						return (
							<SelectOption
								key={key}
								value={key}
							>
								{speechCallTypeList[key]}
							</SelectOption>
						);
					})}
				</Select>
				<MultiSelect
					selectedValues={speechId}
					searchValue={speechIdSearchValue}
					onChange={(val: any) => {
						if(val?.length){
							const result = val.filter((el:string) => el.trim().length > 0);
							if(result?.length) setSpeechId(result);
						}else setSpeechId(val);
						setSpeechIdSearchValue("");
						debouncedChange("");
					}}
					onSearch={(e:string) => {
						setSpeechIdSearchValue(e);
						debouncedChange(e);
					}}
					defaultValue={speechIdTags}
					placeholder={t('KLR00721')}
					className={"mb-3"}
					mode={'multiple'}
					maxTagCount={3}
				>
					{!isEmpty(speechIds) &&
						Object.values(speechIds).map((option:any) => {
							return (
								<MultiSelectOption key={option?.id} value={option?.id}>
									{option?.id}
								</MultiSelectOption>
							);
						})}
				</MultiSelect>
			</div>
		</Slider>
	);
};

export default FilterTemplate;
